import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, CTABand } from "@/components/PageHero";
import { Section } from "@/components/Primitives";
import { fetchInsights } from "@/lib/api";

const CATEGORIES = ["All", "Labuan Company Setup", "Malaysia Business", "International Expansion", "Business Purchase", "Investment Migration", "Canada Business", "UK Business", "Compliance Updates"];

export default function Insights() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchInsights({ category, q }).then((d) => setArticles(d.articles)).finally(() => setLoading(false));
  }, [category, q]);

  return (
    <>
      <SEO title="Insights — Knowledge Centre" description="Guides and articles on Labuan setup, Malaysian business, international expansion and compliance. Contact us to know more." path="/insights" />
      <PageHero eyebrow="Knowledge Centre" title="Insights"
        subtitle="Practical, pre-reviewed guidance on Labuan, Malaysia and international expansion. This is general information only, not legal, tax or immigration advice." />

      <Section className="bg-white">
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="insights-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…"
              className="w-full rounded-full border border-slate-200 pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-brand" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${category === c ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-slate-400 text-center py-10">Loading…</p>
        ) : articles.length === 0 ? (
          <p data-testid="insights-empty" className="text-slate-400 text-center py-10">No articles found. Contact us to know more.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`insight-${a.slug}`} className="group rounded-2xl bg-white border border-slate-100 overflow-hidden hover-lift">
                <div className="h-44 overflow-hidden"><img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-6">
                  <span className="text-xs font-bold text-emerald-brand uppercase tracking-wide">{a.category}</span>
                  <h3 className="mt-2 font-bold text-navy leading-snug group-hover:text-emerald-brand transition-colors">{a.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 line-clamp-2">{a.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Clock size={12} /> {a.reading_time}</span>
                    <span className="flex items-center gap-1 text-emerald-brand font-semibold">Read <ArrowRight size={12} /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
      <CTABand />
    </>
  );
}
